import React, { forwardRef } from 'react';
import { useKeyboardShortcut } from 'src/hooks';
import * as S from '../TestButton/styles';

// Allow `any`s for awkward styled-components types
/* eslint @typescript-eslint/no-explicit-any: "off" */

export const ButtonColor = {
  GREEN: 'green',
  GREY: 'grey',
  RED: 'red',
} as const;
export type ButtonColor = (typeof ButtonColor)[keyof typeof ButtonColor];

export interface BaseButtonProps {
  buttonColor: ButtonColor;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  /**
   * A numerical position which shows a small icon and enables
   * pressing the given number on the keyboard to click the button
   */
  positionLabel: number;
  /** Adds a pale highlight colour to the background of the button */
  highlight: boolean;
  /** Solid light surface so the label stays readable over a full-screen video. */
  onVideo?: boolean;
  /** When false, number-key shortcuts are not bound (label still shows). Defaults to true. */
  enableKeyboardShortcut?: boolean;
}

interface ButtonProps extends React.HTMLProps<HTMLButtonElement>, BaseButtonProps {
  onClick?: (event?: React.MouseEvent<HTMLButtonElement>) => void | Promise<void> | null;
}

export const TestButton = forwardRef(function InnerButton(
  {
    as,
    buttonColor,
    children,
    className,
    disabled,
    onClick,
    positionLabel,
    highlight,
    onVideo = false,
    enableKeyboardShortcut = true,
    ...rest
  }: ButtonProps,
  ref,
) {
  useKeyboardShortcut(
    positionLabel.toString(),
    enableKeyboardShortcut ? () => onClick && !disabled && onClick() : undefined,
  );

  return (
    <S.Variables
      styleProps={{
        disabled: !!disabled,
        $highlight: highlight,
        $onVideo: onVideo,
        $buttonColor: buttonColor,
      }}
    >
      <S.Container className={className}>
        <S.Button
          disabled={!!disabled}
          onClick={onClick}
          type="button"
          ref={ref}
          as={as as any}
          {...rest}
        >
          <S.PositionLabel>{positionLabel}</S.PositionLabel>
          <S.ButtonChildren>{children}</S.ButtonChildren>
        </S.Button>
      </S.Container>
    </S.Variables>
  );
});
